import { useEffect } from 'react';
import Navigation from '../home/components/Navigation';
import Footer from '../home/components/Footer';

export default function OrganizationalStrategy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const areasOfSupport = [
    {
      icon: 'ri-compass-line',
      title: 'Strategic Planning and Organizational Direction',
      description: 'Facilitating strategic planning processes that help organizations clarify priorities, define long-term goals, and build shared leadership ownership of strategy.'
    },
    {
      icon: 'ri-organization-chart',
      title: 'Organizational Design',
      description: 'Advising leaders on organizational structure, leadership roles, and decision-making frameworks that support effective execution of strategy.'
    },
    {
      icon: 'ri-team-line',
      title: 'People Strategy and Talent Systems',
      description: 'Designing leadership development, talent management, and culture strategies that support organizational priorities and strengthen leadership capacity.'
    },
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Compensation Strategy and Pay Equity',
      description: 'Supporting organizations in developing equitable and transparent compensation frameworks that align with organizational values and strengthen talent retention.'
    },
    {
      icon: 'ri-search-eye-line',
      title: 'Organizational Assessments and Diagnostics',
      description: 'Conducting structured assessments that help leaders understand organizational strengths, gaps, and opportunities for improvement. These diagnostics can include leadership effectiveness reviews, organizational health assessments, and people system evaluations.'
    }
  ];

  const typicalEngagements = [
    'Strategic planning and priority-setting processes',
    'Organizational design and leadership structure reviews',
    'People strategy and talent system development',
    'Compensation strategy and pay equity assessments',
    'Organizational health and leadership diagnostics',
    'Implementation planning and strategy alignment'
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-[#1a2b4a] via-[#1a2b4a] to-[#2d9b9b]/20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 border-4 border-white rounded-full -mr-48 -mt-48"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 border-4 border-white rounded-full -ml-32 -mb-32"></div>
        </div>

        <div className="max-w-5xl mx-auto px-6 lg:px-12 relative z-10">
          <div className="text-center">
            <span className="text-[#2d9b9b] text-sm font-semibold tracking-wide uppercase">Organizational Strategy</span>
            <h1 className="text-5xl lg:text-6xl font-bold text-white mt-6 mb-8 leading-tight" style={{ fontFamily: 'Crimson Text, serif' }}>
              Building Clarity, Alignment, and Strategic Direction
            </h1>
            <p className="text-xl text-white/90 leading-relaxed max-w-3xl mx-auto">
              Mission-driven organizations often reach moments when their strategy, leadership, and internal systems need to evolve in order to meet new challenges and opportunities. Jerilyn Emerson partners with nonprofit and social impact leaders to clarify organizational direction, strengthen internal alignment, and build the structures needed to deliver lasting impact.
            </p>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <div className="space-y-6 text-lg text-[#1a2b4a]/70 leading-relaxed">
            <p>
              Organizational strategy engagements help leaders step back from the day-to-day demands of their work to examine how strategy, leadership, and people systems fit together. Through structured dialogue, organizational assessment, and strategic planning processes, Jerilyn helps organizations move from complexity and uncertainty toward clarity, alignment, and forward momentum.
            </p>
            <p>
              Organizations often engage Jerilyn when they are navigating leadership transitions, preparing for growth, rethinking organizational priorities, or seeking to align internal systems with their mission and long-term goals.
            </p>
          </div>
        </div>
      </section>

      {/* Areas of Strategic Support Section */}
      <section className="py-20 bg-[#f8f9fa]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-[#1a2b4a] mb-6" style={{ fontFamily: 'Crimson Text, serif' }}>
              Areas of Strategic Support
            </h2>
            <p className="text-lg text-[#1a2b4a]/70 max-w-3xl mx-auto leading-relaxed">
              Strategic support is tailored to each organization's needs and may include:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {areasOfSupport.map((area, index) => (
              <div
                key={index}
                className="bg-white rounded-3xl p-10 shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="w-12 h-12 flex items-center justify-center mb-6">
                  <i className={`${area.icon} text-5xl text-[#2d9b9b]`}></i>
                </div>
                <h3 className="text-xl font-bold text-[#1a2b4a] mb-4">
                  {area.title}
                </h3>
                <p className="text-base text-gray-600 leading-relaxed">
                  {area.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Approach Section */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <h2 className="text-4xl font-bold text-[#1a2b4a] mb-8" style={{ fontFamily: 'Crimson Text, serif' }}>
            Approach
          </h2>
          
          <div className="space-y-6 text-lg text-[#1a2b4a]/70 leading-relaxed">
            <p>
              Jerilyn brings both executive leadership experience and organizational development expertise to her strategy work. Having served as Chief Operating Officer, Chief People & Culture Officer, and Chief of Staff at Greenpeace USA, she understands the complexity of aligning strategy, operations, and people systems inside large mission-driven organizations.
            </p>
            <p>
              Her approach combines strategic facilitation, organizational diagnostics, and leadership advisory to help leaders surface key issues, make informed decisions, and build stronger alignment across their organizations.
            </p>
            <p>
              Each engagement is tailored to the organization's needs and may include stakeholder interviews, leadership team discussions, organizational assessments, strategic planning workshops, and implementation planning.
            </p>
          </div>
        </div>
      </section>

      {/* Typical Engagements Section */}
      <section className="py-20 bg-[#f8f9fa]">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <h2 className="text-4xl font-bold text-[#1a2b4a] mb-8" style={{ fontFamily: 'Crimson Text, serif' }}>
            Typical Engagements
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {typicalEngagements.map((engagement, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 shadow-md hover:shadow-lg transition-all duration-300 border-l-4 border-[#2d9b9b]"
              >
                <p className="text-base text-[#1a2b4a] leading-relaxed font-medium">
                  {engagement}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-12 p-8 bg-white rounded-2xl shadow-md">
            <p className="text-lg text-[#1a2b4a]/70 leading-relaxed">
              Organizations typically engage Jerilyn when they need an experienced advisor to help leadership teams step back, clarify strategy, and build the internal systems necessary to deliver meaningful impact.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-[#2d9b9b]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Crimson Text, serif' }}>
            Connect
          </h2>
          <p className="text-xl text-white/90 leading-relaxed mb-10">
            If your organization is navigating an important strategic moment and would benefit from experienced guidance, reach out to start a conversation.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
            <a
              href="mailto:jerilyn@jerilynemerson.com"
              className="inline-flex items-center gap-3 bg-white text-[#2d9b9b] px-8 py-4 rounded-full hover:bg-[#1a2b4a] hover:text-white transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-line text-xl"></i>
              jerilyn@jerilynemerson.com
            </a>
            <a
              href="https://linkedin.com/in/jerilynemerson"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 border-2 border-white text-white px-8 py-4 rounded-full hover:bg-white hover:text-[#2d9b9b] transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-linkedin-line text-xl"></i>
              LinkedIn
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}